import type {
  FilterClause,
  FilterConfig,
  FilterOp,
  FilterParameter,
} from "../../store/api/dataSourcesApi";

export const FILTER_OPS: { value: FilterOp; label: string }[] = [
  { value: "eq", label: "equals" },
  { value: "neq", label: "not equals" },
  { value: "in", label: "in" },
  { value: "not_in", label: "not in" },
  { value: "gt", label: "greater than" },
  { value: "gte", label: "greater or equal" },
  { value: "lt", label: "less than" },
  { value: "lte", label: "less or equal" },
  { value: "or_group", label: "OR group" },
];

export const BUILTIN_FILTER_SOURCES: { value: string; label: string }[] = [
  { value: "consumer_org_id", label: "Consumer organisation" },
  { value: "consumer_user_id", label: "Consumer user" },
  { value: "org_id", label: "Lumina organisation" },
  { value: "user_id", label: "Caller user" },
  { value: "application_id", label: "Application" },
  { value: "custom", label: "Custom request parameter" },
];

export function emptyFilterConfig(): FilterConfig {
  return { parameters: [], clauses: [] };
}

export function isEmptyFilterConfig(config: FilterConfig | null | undefined): boolean {
  if (!config) return true;
  const params = config.parameters ?? [];
  const clauses = config.clauses ?? [];
  return params.length === 0 && clauses.length === 0;
}

export function clauseValueFrom(clause: FilterClause): string {
  return (clause.valueFrom || clause.value_from || "").trim();
}

/** Payload shape expected by portal create/update (snake_case). */
export function toFilterConfigPayload(
  config: FilterConfig | null | undefined,
): FilterConfig | undefined {
  if (!config || isEmptyFilterConfig(config)) return undefined;
  return {
    parameters: (config.parameters ?? []).map((p) => ({
      name: p.name.trim(),
      source: p.source.trim(),
      required: Boolean(p.required),
      label: p.label?.trim() || p.name,
      description: p.description ?? null,
    })),
    clauses: (config.clauses ?? []).map((c) => serializeClause(c)),
  };
}

function serializeClause(c: FilterClause): FilterClause {
  if (c.op === "or_group") {
    return {
      op: "or_group",
      clauses: (c.clauses ?? []).map((child) => serializeClause(child)),
    };
  }
  const from = clauseValueFrom(c);
  const out: FilterClause = {
    field: (c.field || "").trim(),
    op: c.op || "eq",
  };
  if (from) {
    out.value_from = from;
  } else if (c.op === "in" || c.op === "not_in") {
    const raw = c.value;
    out.value = Array.isArray(raw)
      ? raw
      : String(raw ?? "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
  } else {
    out.value = c.value;
  }
  return out;
}

export function sourceSelectValue(source: string): string {
  if (!source) return "consumer_org_id";
  if (source.startsWith("parameters.")) return "custom";
  return source;
}

export function customParamName(source: string): string {
  return source.startsWith("parameters.") ? source.slice("parameters.".length) : "";
}

export function defaultParameter(): FilterParameter {
  return {
    name: "consumer_org_id",
    source: "consumer_org_id",
    required: true,
    label: "Consumer organisation",
  };
}

export function defaultClause(paramName?: string): FilterClause {
  if (paramName) {
    return { field: "", op: "eq", value_from: paramName };
  }
  return { field: "visibility", op: "eq", value: "public" };
}

export function describeFilterScope(
  config: FilterConfig | null | undefined,
): string {
  if (!config?.clauses?.length) return '';
  return config.clauses.map(describeFilterClause).filter(Boolean).join(' AND ');
}

function describeFilterClause(clause: FilterClause): string {
  if (clause.op === 'or_group') {
    const inner = (clause.clauses ?? [])
      .map(describeFilterClause)
      .filter(Boolean)
      .join(' OR ');
    return inner ? `(${inner})` : '';
  }
  const field = clause.field || '';
  const bound = clauseValueFrom(clause);
  const rhs =
    bound ||
    (clause.value == null ? '' : JSON.stringify(clause.value));
  const op = clause.op === 'eq' ? '=' : clause.op || 'eq';
  return [field, op, rhs].filter(Boolean).join(' ');
}

function clauseUsesListOp(clauses: FilterClause[] | undefined, paramName: string): boolean {
  return (clauses ?? []).some((clause) => {
    if (clause.op === 'or_group') {
      return clauseUsesListOp(clause.clauses, paramName);
    }
    return (
      clauseValueFrom(clause) === paramName &&
      (clause.op === 'in' || clause.op === 'not_in')
    );
  });
}

export function paramUsesListBinding(
  paramName: string,
  configs: Array<FilterConfig | null | undefined>,
): boolean {
  return configs.some((config) => clauseUsesListOp(config?.clauses, paramName));
}

function parseListParam(raw: string): string[] {
  const text = raw.trim();
  if (text.startsWith('[') && text.endsWith(']')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      /* fall through to comma split */
    }
  }
  return text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Chat body `parameters` — list bindings become string arrays. */
export function toChatQueryParameters(
  raw: Record<string, string>,
  configs: Array<FilterConfig | null | undefined>,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(raw)) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    out[key] = paramUsesListBinding(key, configs)
      ? parseListParam(trimmed)
      : trimmed;
  }
  return out;
}

export function formatCodeExecutionLog(event: {
  type?: string;
  data?: Record<string, unknown>;
}): string {
  const data = event.data || {};
  const parts = [event.type || 'code.execution'];
  const language = String(data.language ?? '').trim();
  const executionId = String(data.execution_id ?? data.executionId ?? '').trim();
  const exitStatus = data.exit_status ?? data.exitStatus;
  const error = String(data.error ?? data.error_summary ?? '').trim();
  if (language) parts.push(language);
  if (executionId) parts.push(executionId);
  if (exitStatus !== undefined && exitStatus !== null && exitStatus !== '') {
    parts.push(`exit ${exitStatus}`);
  }
  if (error) parts.push(`error: ${error}`);
  return parts.join(' · ');
}

export function formatDatasourceQueryLog(event: {
  type?: string;
  data?: Record<string, unknown>;
}): string {
  const data = event.data || {};
  const appliedRaw = data.appliedFilters ?? data.applied_filters;
  const applied = Array.isArray(appliedRaw)
    ? appliedRaw
        .map((clause) => {
          const row = clause as { field?: string; op?: string };
          return row.field ? `${row.field} ${row.op}` : row.op;
        })
        .filter(Boolean)
        .join(', ')
    : '';
  const summary = String(data.querySummary ?? data.query_summary ?? '').trim();
  const error = String(data.error ?? '').trim();
  const parts = [event.type || 'datasource.query'];
  if (applied) parts.push(`scope: ${applied}`);
  if (summary) parts.push(summary);
  if (error) parts.push(`error: ${error}`);
  return parts.join(' · ');
}

export function defaultOrGroupClause(): FilterClause {
  return {
    op: "or_group",
    clauses: [{ field: "visibility", op: "eq", value: "public" }],
  };
}
